/**
 * Dynamic Google Font loader for the Funnel PRO theme.
 *
 * Injects <link> tags for Google Fonts based on the theme's typography config.
 * Each font is loaded at most once per page session (idempotent).
 */

import type { ThemeTypography } from '@/types/theme.types';
import { FONT_WEIGHT_SUFFIX_RE } from '@/config/theme-editor.constants';

/** Map of font identifiers → Google Fonts CSS URLs */
export const GOOGLE_FONT_URLS: Record<string, string> = {
  poppins: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap',
  inter: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap',
  montserrat: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap',
  open_sans: 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;500;600;700;800&display=swap',
  roboto: 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700;900&display=swap',
  lato: 'https://fonts.googleapis.com/css2?family=Lato:wght@400;700;900&display=swap',
  raleway: 'https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800&display=swap',
  playfair_display: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&display=swap',
  oswald: 'https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&display=swap',
  nunito: 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap',
  dm_sans: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap',
};

export function injectFontLink(fontKey: string) {
  const url = GOOGLE_FONT_URLS[fontKey];
  if (!url) return;
  const id = `gfont-${fontKey}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = url;
  document.head.appendChild(link);
}

/** Extract the font family key from a shopifyFont value (strips _nN suffix) */
function fontKey(shopifyFont?: string): string | null {
  if (!shopifyFont) return null;
  return shopifyFont.replace(FONT_WEIGHT_SUFFIX_RE, '');
}

/** Load Google Fonts for both heading and body fonts from the theme typography config */
export function loadThemeFonts(typography: ThemeTypography) {
  const headingKey = fontKey(typography.headerFont?.shopifyFont);
  if (headingKey) injectFontLink(headingKey);

  const bodyKey = fontKey(typography.bodyFont?.shopifyFont);
  if (bodyKey) injectFontLink(bodyKey);

  // Custom font links (user-provided URLs)
  if (typography.headerFont?.type === 'custom' && typography.headerFont.customFontLink) {
    injectCustomFontLink('custom-heading', typography.headerFont.customFontLink);
  }
  if (typography.bodyFont?.type === 'custom' && typography.bodyFont.customFontLink) {
    injectCustomFontLink('custom-body', typography.bodyFont.customFontLink);
  }
}

function injectCustomFontLink(id: string, href: string) {
  const existing = document.getElementById(id) as HTMLLinkElement | null;
  if (existing) {
    if (existing.href === href) return;
    existing.href = href;
    return;
  }
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}
